import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import Reveal from "@/components/Reveal";

const GENRES = ["Literary Fiction", "Romance", "Memoir", "Historical", "Thriller/Mystery", "Poetry", "Self-Development", "Non-Fiction", "Fantasy/Sci-Fi", "Young Adult"];
const HEARD = ["Instagram", "Facebook", "TikTok", "A Friend", "Google Search", "An Event", "Other"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Join() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", password: "", city: "", state: "",
    birthday: "", genres: [], hopes: "", referral: "", email_consent: true, agree_terms: false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleGenre = (g) => setF({ ...f, genres: f.genres.includes(g) ? f.genres.filter((x) => x !== g) : [...f.genres, g] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agree_terms) { toast.error("Please agree to the Terms & Conditions and Privacy Policy to join."); return; }
    setBusy(true);
    try {
      const { agree_terms, ...payload } = f;
      await register(payload);
      setDone(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mahogany-950 to-mahogany-800 px-6 pt-28 pb-16 grain" data-testid="join-success">
        <Reveal className="max-w-xl text-center">
          <Logo size="md" />
          <p className="font-script text-4xl text-fuchsia mt-8 mb-4">Welcome to The Mahogany Room.</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-cream mb-6">Pull up a chair.<br />Your next chapter starts here.</h1>
          <p className="text-cream/70 mb-10">Check your inbox for a warm welcome — then step inside your private reading lounge.</p>
          <button onClick={() => navigate("/dashboard")} className="btn-primary" data-testid="go-to-dashboard">Enter Your Room</button>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-mahogany-950 to-mahogany-900 pt-32 pb-20 px-6 grain" data-testid="join-page">
      <div className="max-w-2xl mx-auto">
        <Reveal className="text-center mb-10">
          <p className="font-script text-3xl text-fuchsia mb-2">Free to Join</p>
          <h1 className="font-serif text-5xl text-cream mb-3">Join The Room</h1>
          <p className="text-cream/60">Membership is free. Pull up a chair.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <button onClick={googleLogin} className="btn-secondary w-full mb-6" data-testid="google-signup">Continue with Google</button>
          <div className="flex items-center gap-4 mb-6 text-cream/40 text-xs uppercase tracking-widest"><span className="flex-1 h-px bg-gold/20" /> or sign up with email <span className="flex-1 h-px bg-gold/20" /></div>

          <form onSubmit={submit} className="bg-mahogany-950 gold-frame rounded-sm p-8 space-y-6" data-testid="join-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <input required placeholder="First Name" value={f.first_name} onChange={set("first_name")} className="input-line" data-testid="join-first-name" />
              <input required placeholder="Last Name" value={f.last_name} onChange={set("last_name")} className="input-line" data-testid="join-last-name" />
            </div>
            <input required type="email" placeholder="Email Address" value={f.email} onChange={set("email")} className="input-line" data-testid="join-email" />
            <input required type="password" placeholder="Create a Password" value={f.password} onChange={set("password")} className="input-line" data-testid="join-password" />
            <div className="grid sm:grid-cols-2 gap-5">
              <input placeholder="City" value={f.city} onChange={set("city")} className="input-line" data-testid="join-city" />
              <input placeholder="State" value={f.state} onChange={set("state")} className="input-line" data-testid="join-state" />
            </div>
            <div>
              <label className="text-cream/50 text-xs uppercase tracking-wider">Birthday (optional)</label>
              <input type="date" value={f.birthday} onChange={set("birthday")} className="input-line" data-testid="join-birthday" />
            </div>
            <div>
              <label className="text-cream/50 text-xs uppercase tracking-wider block mb-3">Favorite Literary Genres</label>
              <div className="flex flex-wrap gap-2">
                {GENRES.map((g) => (
                  <button type="button" key={g} onClick={() => toggleGenre(g)} data-testid={`genre-${g.slice(0, 6).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f.genres.includes(g) ? "bg-fuchsia border-fuchsia text-white" : "border-gold/30 text-cream/70 hover:border-gold"}`}>{g}</button>
                ))}
              </div>
            </div>
            <textarea rows={3} placeholder="What are you hoping to find in The Mahogany Room?" value={f.hopes} onChange={set("hopes")} className="input-line resize-none" data-testid="join-hopes" />
            <div>
              <label className="text-cream/50 text-xs uppercase tracking-wider block mb-2">How did you hear about us?</label>
              <select value={f.referral} onChange={set("referral")} className="input-line" data-testid="join-referral">
                <option value="" className="bg-mahogany-900">Select one</option>
                {HEARD.map((h) => <option key={h} value={h} className="bg-mahogany-900">{h}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-3 text-cream/60 text-sm cursor-pointer">
              <input type="checkbox" checked={f.email_consent} onChange={(e) => setF({ ...f, email_consent: e.target.checked })} className="mt-1 accent-fuchsia" data-testid="join-consent" />
              <span>Yes, send me book lists, gathering announcements, community updates, and other Mahogany Room communications.</span>
            </label>
            <label className="flex items-start gap-3 text-cream/70 text-sm cursor-pointer">
              <input type="checkbox" checked={f.agree_terms} onChange={(e) => setF({ ...f, agree_terms: e.target.checked })} className="mt-1 accent-fuchsia" data-testid="join-agree-terms" />
              <span>I agree to the <Link to="/terms" target="_blank" className="text-fuchsia hover:text-gold underline underline-offset-2">Terms &amp; Conditions</Link> and <Link to="/privacy" target="_blank" className="text-fuchsia hover:text-gold underline underline-offset-2">Privacy Policy</Link>.</span>
            </label>
            <button type="submit" disabled={busy || !f.agree_terms} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="join-submit">{busy ? "Creating your account..." : "Join The Room"}</button>
            <p className="text-center text-cream/50 text-sm">Already a member? <Link to="/login" className="text-fuchsia">Sign in</Link></p>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
